import { useTranslation } from "react-i18next";
import { Check, RotateCcw, SlidersHorizontal } from "lucide-react";
import type { PlatformTypeName } from "@multi-chat/shared";
import { Button } from "../../components/ui/button/index.js";
import { ChannelIdentityBadge } from "../../components/channel/ChannelIdentityBadge.js";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover/index.js";
import { cn } from "../../lib/utils.js";
import type { ContactsFilterState } from "./contacts-list-query.js";
import { contactsViewportPopoverClass } from "./contacts-table-presentation.js";

const PRESENCE_OPTIONS = ["ANY", "HAS", "MISSING"] as const;
const PLATFORM_OPTIONS = ["ANY", "WHATSAPP", "FACEBOOK", "INSTAGRAM"] as const;

function FilterOptionButton({
  selected,
  onSelect,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-[0.8125rem] leading-5 transition-colors hover:bg-surface-muted",
        selected ? "bg-surface-muted font-medium text-foreground" : "font-normal text-foreground/90",
      )}
      aria-pressed={selected}
      onClick={onSelect}
    >
      <span className="inline-flex size-4 shrink-0 items-center justify-center text-accent">
        {selected ? <Check size={14} aria-hidden="true" /> : null}
      </span>
      <span className="inline-flex min-w-0 flex-1 items-center gap-2">{children}</span>
    </button>
  );
}

export function ContactsFilterPopover({
  filters,
  activeCount,
  onChange,
  onClearAll,
}: {
  filters: ContactsFilterState;
  activeCount: number;
  onChange: (next: ContactsFilterState) => void;
  onClearAll: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const dir = i18n.dir() === "rtl" ? "rtl" : "ltr";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-[0.75rem]" data-testid="contacts-filter">
          <SlidersHorizontal size={14} aria-hidden="true" />
          {t("contacts.actions.filter")}
          {activeCount > 0 ? (
            <span className="ms-0.5 rounded-full bg-accent-soft px-1.5 py-0.5 text-[0.6875rem] font-semibold text-accent">
              {activeCount}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        dir={dir}
        collisionPadding={12}
        className={cn("w-[min(18.5rem,calc(100vw-1.5rem))] p-3", contactsViewportPopoverClass)}
        data-testid="contacts-filter-popover"
      >
        <div className="grid gap-3">
          <section className="grid gap-1">
            <h3 className="m-0 px-2 text-[0.6875rem] font-semibold tracking-[0.04em] text-muted uppercase">
              {t("contacts.filters.email")}
            </h3>
            {PRESENCE_OPTIONS.map((value) => (
              <FilterOptionButton
                key={`email-${value}`}
                selected={
                  value === "ANY" ? filters.hasEmail == null : filters.hasEmail === value
                }
                onSelect={() =>
                  onChange({ ...filters, hasEmail: value === "ANY" ? undefined : value })
                }
              >
                {t(`contacts.filters.hasEmail.${value.toLowerCase()}`)}
              </FilterOptionButton>
            ))}
          </section>

          <section className="grid gap-1 border-t border-border/60 pt-3">
            <h3 className="m-0 px-2 text-[0.6875rem] font-semibold tracking-[0.04em] text-muted uppercase">
              {t("contacts.filters.phone")}
            </h3>
            {PRESENCE_OPTIONS.map((value) => (
              <FilterOptionButton
                key={`phone-${value}`}
                selected={
                  value === "ANY" ? filters.hasPhone == null : filters.hasPhone === value
                }
                onSelect={() =>
                  onChange({ ...filters, hasPhone: value === "ANY" ? undefined : value })
                }
              >
                {t(`contacts.filters.hasPhone.${value.toLowerCase()}`)}
              </FilterOptionButton>
            ))}
          </section>

          <section className="grid gap-1 border-t border-border/60 pt-3">
            <h3 className="m-0 px-2 text-[0.6875rem] font-semibold tracking-[0.04em] text-muted uppercase">
              {t("contacts.filters.platform")}
            </h3>
            {PLATFORM_OPTIONS.map((value) => {
              const selected =
                value === "ANY" ? filters.platform == null : filters.platform === value;
              return (
                <FilterOptionButton
                  key={`platform-${value}`}
                  selected={selected}
                  onSelect={() =>
                    onChange({
                      ...filters,
                      platform: value === "ANY" ? undefined : value,
                    })
                  }
                >
                  {value === "ANY" ? (
                    t("contacts.filters.platformOptions.any")
                  ) : (
                    <>
                      <ChannelIdentityBadge
                        platformType={value as PlatformTypeName}
                        size="filter"
                      />
                      <span>{t(`contacts.filters.platformOptions.${value.toLowerCase()}`)}</span>
                    </>
                  )}
                </FilterOptionButton>
              );
            })}
          </section>

          <section className="grid gap-2 border-t border-border/60 pt-3">
            <h3 className="m-0 px-2 text-[0.6875rem] font-semibold tracking-[0.04em] text-muted uppercase">
              {t("contacts.filters.createdDate")}
            </h3>
            <label className="grid gap-1 px-2">
              <span className="text-[0.75rem] text-muted">{t("contacts.filters.createdFrom")}</span>
              <input
                type="date"
                dir="ltr"
                className="h-8 w-full rounded-md border border-line/70 bg-background px-2 text-[0.8125rem] text-start"
                value={filters.createdFrom ?? ""}
                onChange={(event) =>
                  onChange({
                    ...filters,
                    createdFrom: event.target.value || undefined,
                  })
                }
              />
            </label>
            <label className="grid gap-1 px-2">
              <span className="text-[0.75rem] text-muted">{t("contacts.filters.createdTo")}</span>
              <input
                type="date"
                dir="ltr"
                className="h-8 w-full rounded-md border border-line/70 bg-background px-2 text-[0.8125rem] text-start"
                value={filters.createdTo ?? ""}
                onChange={(event) =>
                  onChange({
                    ...filters,
                    createdTo: event.target.value || undefined,
                  })
                }
              />
            </label>
            {filters.createdFrom || filters.createdTo ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 justify-start"
                onClick={() =>
                  onChange({ ...filters, createdFrom: undefined, createdTo: undefined })
                }
              >
                {t("contacts.filters.clearDates")}
              </Button>
            ) : null}
          </section>

          {activeCount > 0 ? (
            <div className="border-t border-border/60 pt-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-full justify-start"
                onClick={onClearAll}
              >
                <RotateCcw size={14} aria-hidden="true" />
                {t("contacts.filters.clearAll")}
              </Button>
            </div>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
